/**
 * Macro recorder module.
 * Filters and enriches API calls for macro recording.
 *
 * Only workflow operations are recorded (putPayload, getPayload, markPayloadComplete).
 * Auth calls, key lookups, and admin operations are filtered out.
 */

import { setRecordCallback } from '../api/client.js';
import { addRecordedAction, isRecording } from '../state/macro.js';

/**
 * Path patterns to record (workflow operations only).
 * Maps regex patterns to operation names.
 */
const RECORDABLE_PATTERNS = [
  // PUT /payloads/{sampleId}/{stageId} or /payloads/{workflowId}/{sampleId}/{stageId}
  { method: 'PUT', pattern: /^\/payloads\/([^/]+)\/([^/]+)(?:\/([^/]+))?$/, operation: 'put_payload' },
  // GET /payloads/{sampleId}/{stageId} or /payloads/{workflowId}/{sampleId}/{stageId} (not /complete)
  { method: 'GET', pattern: /^\/payloads\/([^/]+)\/([^/]+)(?:\/([^/]+))?$/, operation: 'get_payload' },
  // POST /payloads/.../complete
  { method: 'POST', pattern: /^\/payloads\/([^/]+)\/([^/]+)\/complete$/, operation: 'mark_complete' },
  { method: 'POST', pattern: /^\/payloads\/([^/]+)\/([^/]+)\/([^/]+)\/complete$/, operation: 'mark_complete' }
];

/**
 * Paths to explicitly skip.
 * These are filtered out even if they might match a pattern.
 */
const SKIP_PATTERNS = [
  /^\/$/, // Health check
  /^\/parties\/[^/]+\/public-key$/, // Key lookups (internal to write flow)
  /^\/admin\//i, // All admin paths
  /^\/super-admin\//i // All super-admin paths
];

/**
 * Check if a path should be skipped (not recorded).
 * @param {string} path - API path
 * @returns {boolean} True if should be skipped
 */
function shouldSkip(path) {
  return SKIP_PATTERNS.some(pattern => pattern.test(path));
}

/**
 * Parse path parameters from a payload path.
 * Handles both 2-param (/payloads/sample/stage) and 3-param (/payloads/workflow/sample/stage) formats.
 * @param {string} path - API path
 * @returns {{workflowId: string|null, sampleId: string, stageId: string}|null} Parsed params or null
 */
function parsePathParams(path) {
  // Remove /complete suffix for parsing
  const cleanPath = path.replace(/\/complete$/, '');

  // Try 3-param format: /payloads/{workflowId}/{sampleId}/{stageId}
  const threeParam = /^\/payloads\/([^/]+)\/([^/]+)\/([^/]+)$/.exec(cleanPath);
  if (threeParam) {
    return {
      workflowId: decodeURIComponent(threeParam[1]),
      sampleId: decodeURIComponent(threeParam[2]),
      stageId: decodeURIComponent(threeParam[3])
    };
  }

  // Try 2-param format: /payloads/{sampleId}/{stageId}
  const twoParam = /^\/payloads\/([^/]+)\/([^/]+)$/.exec(cleanPath);
  if (twoParam) {
    return {
      workflowId: null,
      sampleId: decodeURIComponent(twoParam[1]),
      stageId: decodeURIComponent(twoParam[2])
    };
  }

  return null;
}

/**
 * Parse a raw API action into a semantic recorded action.
 * Returns null if the action should not be recorded.
 *
 * @param {Object} rawAction - Raw action from API client
 * @param {string} rawAction.method - HTTP method
 * @param {string} rawAction.path - API path
 * @param {Object|null} rawAction.body - Request body
 * @param {boolean} rawAction.success - Whether request succeeded
 * @param {number} rawAction.status - HTTP status code
 * @param {string|null} rawAction.error - Error message if failed
 * @returns {Object|null} Semantic action or null if should not be recorded
 */
export function parseAction(rawAction) {
  const { method, path, body, success, status, error } = rawAction;

  // Check if path should be skipped
  if (shouldSkip(path)) {
    return null;
  }

  // Find matching recordable pattern
  let operation = null;
  for (const { method: m, pattern, operation: op } of RECORDABLE_PATTERNS) {
    if (m === method && pattern.test(path)) {
      operation = op;
      break;
    }
  }

  // Not a recordable operation
  if (!operation) {
    return null;
  }

  // Parse path parameters
  const pathParams = parsePathParams(path);
  if (!pathParams) {
    return null;
  }

  // Build params object with sanitized data
  const params = {
    workflowId: pathParams.workflowId,
    sampleId: pathParams.sampleId,
    stageId: pathParams.stageId
  };

  // Extract readerId from body for put_payload
  if (operation === 'put_payload' && body) {
    params.readerId = body.reader_id || null;
  }

  // Determine if encryption is needed (put_payload needs encryption)
  const needsEncryption = operation === 'put_payload';

  return {
    id: crypto.randomUUID(),
    operation,
    method,
    path,
    params,
    needsEncryption,
    success,
    status,
    error: error || null
  };
}

/**
 * Recording callback for API client.
 * Checks if recording is active, parses the action, and adds to state.
 * @param {Object} rawAction - Raw action from API client
 */
function handleApiCall(rawAction) {
  // Only record if recording is active
  if (!isRecording()) {
    return;
  }

  // Parse and filter action
  const action = parseAction(rawAction);

  // Only add if it's a recordable action
  if (action) {
    addRecordedAction(action);
  }
}

/**
 * Initialize the macro recorder.
 * Registers the recording callback with the API client.
 * @returns {Function} Cleanup function to remove the callback
 */
export function initRecorder() {
  setRecordCallback(handleApiCall);
  return cleanupRecorder;
}

/**
 * Clean up the macro recorder.
 * Removes the recording callback from the API client.
 */
export function cleanupRecorder() {
  setRecordCallback(null);
}
